/**
 * The page before you sign in.
 *
 * THE DOOR WAS ONE LINE WIDE. Home's signed-out branch was the word "Reporting", a sentence, and
 * "Sign in to continue." -- which was deliberate, and the reasoning in Home.tsx still holds: the
 * clone arrived with t5's marketing landing advertising connectors, a glossary and a form designer,
 * none of which this address serves, and that was deleted rather than adapted.
 *
 * WHAT IS ADDED IS NOT THAT. Everything below is in the present tense about something a signed-in
 * user can do today -- shelves, relative dates, calculated and table calculations, the compiled
 * statement, charts, dashboards, schedules, Excel and Parquet -- all of it built and deployed. No
 * pillar, no roadmap, no date. The difference between a landing page and a marketing landing page
 * is whether anything on it is a promise, and nothing here is.
 *
 * NO LOCKUP OF ITS OWN. It had one -- logo, ParkourAI, the product -- and the header
 * directly above it carries exactly that, so the screen said it twice, 250 pixels apart, and
 * the second one read as a mistake. The headline is the first thing on the page instead.
 *
 * THE SHAPE IS SHARED WITH DOCS's. Two apps in one suite reached by two addresses should not look
 * like two companies, so the brand row, the measure, the three cards and the persona line are laid
 * out identically in both and only the words differ. They are separate repositories with no shared
 * package between them, so "identically" is maintained by hand, which is worth knowing before
 * changing one of them.
 */
import React from "react";
import { Box, Button, Paper, Typography } from "@mui/material";

import useAppContext from "../util/AppContext";
import { Blue3, Blue4, Grey2, Grey3, Grey5, Grey7, Grey17, White } from "../theme/styles";

/**
 * What this app does, in three parts.
 *
 * ORDERED AS SOMEBODY MEETS IT: you build one, you put it somewhere people look, and it arrives
 * without being asked for. The scope sentence is last and is the one a quality manager asks about
 * first, because a report that quietly reads past a persona is the only failure here that matters.
 */
const DOES = [
    {
        title: "Build the question",
        text: "Place fields on shelves, group them, filter on relative dates, and add calculated "
            + "fields and table calculations. The statement it compiles to is on the next tab, so "
            + "what the numbers came from is readable rather than taken on trust.",
    },
    {
        title: "Charts and dashboards",
        text: "Turn any result into a chart, place charts and tables on a dashboard, and make one "
            + "dashboard the page your account lands on — so arriving at this address means "
            + "arriving at the numbers.",
    },
    {
        title: "On a schedule",
        text: "Run a saved report on a schedule and have the link emailed, export a result to "
            + "Excel or Parquet, or deliver it to your own bucket as a feed.",
    },
];

export default function Splash() {
    const sessionContext = useAppContext()?.sessionContext;

    // THE SAME CONTROL THE HEADER'S PERSON ICON USES. Setting the flag emits a session update and
    // the navigation bar, which owns the dialog, opens it -- so this button needs no dialog of its
    // own and cannot disagree with that one about whether sign-in is open.
    const signIn = () => sessionContext?.setShowLoginDialog?.(true);

    return (
        <Box sx={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            minHeight: "100%", width: "100%", backgroundColor: White,
            // THIS SUITE HAS NO GLOBAL border-box RULE, deliberately -- so a width of 100% with
            // side padding is 100% PLUS the padding, and the page scrolled sideways by exactly
            // 40px on a phone. Set here rather than globally for the reason the suite does not set
            // it globally: a global change reflows every screen in the app.
            boxSizing: "border-box",
            px: { xs: 2.5, md: 4 }, py: { xs: 6, md: 8 },
        }}>
            <Box sx={{ width: "100%", maxWidth: "960px" }}>
                <Typography component="h1" sx={{
                    fontSize: { xs: 30, md: 42 }, fontWeight: 600, color: Grey17,
                    lineHeight: 1.15, maxWidth: "24ch",
                }}>
                    Ask your own records the question.
                </Typography>

                <Typography sx={{
                    fontSize: { xs: 15, md: 17 }, color: Grey7, mt: 2, maxWidth: "64ch",
                    lineHeight: 1.6,
                }}>
                    Reporting queries what your quality system already holds — including the answers
                    people typed on your forms — and builds analytical views over them.
                </Typography>

                <Button variant="contained" disableElevation onClick={signIn} sx={{
                    mt: { xs: 3.5, md: 4 }, textTransform: "none", fontSize: 15, fontWeight: 500,
                    px: 3, py: 1, backgroundColor: Blue4, "&:hover": { backgroundColor: Blue3 },
                }}>
                    Sign in
                </Button>

                {/* THREE CARDS, WRAPPING RATHER THAN A FIXED GRID, so the page is readable at a
                    phone's width without a second layout to maintain. */}
                <Box sx={{
                    display: "flex", flexWrap: "wrap", gap: 2, mt: { xs: 5, md: 7 },
                }}>
                    {DOES.map((item) => (
                        <Paper key={item.title} variant="outlined" sx={{
                            flex: "1 1 240px", minWidth: 0, p: 2.5,
                            borderColor: Grey3, backgroundColor: Grey2, boxShadow: "none",
                        }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: Grey17 }}>
                                {item.title}
                            </Typography>
                            <Typography sx={{ fontSize: 13.5, color: Grey7, mt: 1, lineHeight: 1.6 }}>
                                {item.text}
                            </Typography>
                        </Paper>
                    ))}
                </Box>

                <Typography sx={{ fontSize: 12.5, color: Grey5, mt: { xs: 4, md: 5 } }}>
                    Part of the ParkourAI suite. Every report runs as you — your persona's
                    visibility is applied inside the query, not after it.
                </Typography>
            </Box>
        </Box>
    );
}
